import React, { useState, useEffect } from 'react';
import { X, Hash, Search, Plus, Trash2, Upload, Download, AlertCircle, CheckCircle, QrCode } from 'lucide-react';
import { 
  getKonzernIds, 
  addKonzernIds, 
  deleteKonzernId, 
  importKonzernIdsFromExcel 
} from '../../lib/localDatabase';
import { KonzernId } from '../../types/building';
import { BarcodeScanner } from '../BarcodeScanner';

interface KonzernIdManagerProps {
  onClose: () => void;
}

export const KonzernIdManager: React.FC<KonzernIdManagerProps> = ({ onClose }) => {
  const [konzernIds, setKonzernIds] = useState<KonzernId[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newKonzernId, setNewKonzernId] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [importMessage, setImportMessage] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Load Konzern IDs
  useEffect(() => {
    loadKonzernIds();
  }, []);

  const loadKonzernIds = () => {
    const ids = getKonzernIds();
    setKonzernIds(ids);
  };

  const handleAddKonzernId = () => {
    if (!newKonzernId.trim()) return;

    try {
      addKonzernIds([newKonzernId.trim()]);
      setNewKonzernId('');
      loadKonzernIds();
    } catch (error) {
      console.error('Error adding Konzern ID:', error);
      alert('Fehler beim Hinzufügen der Konzern ID');
    }
  };

  const handleDeleteKonzernId = (value: string) => {
    try {
      deleteKonzernId(value);
      loadKonzernIds();
    } catch (error) {
      console.error('Error deleting Konzern ID:', error);
      alert(error instanceof Error ? error.message : 'Fehler beim Löschen der Konzern ID');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportStatus('idle');
      setImportMessage('');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportStatus('error');
      setImportMessage('Bitte wählen Sie eine Datei aus');
      return;
    }

    setIsImporting(true);
    setImportStatus('idle');
    setImportMessage('');

    try {
      const newIds = await importKonzernIdsFromExcel(importFile);
      setImportStatus('success');
      setImportMessage(`${newIds.length} Konzern IDs erfolgreich importiert`);
      loadKonzernIds();
      setImportFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('konzern-id-file') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error importing Konzern IDs:', error);
      setImportStatus('error');
      setImportMessage(error instanceof Error ? error.message : 'Fehler beim Importieren der Konzern IDs');
    } finally {
      setIsImporting(false);
    }
  };

  const handleExportTemplate = () => {
    // Create a simple CSV template
    const csvContent = "KonzernID\nBeispiel-ID-001\nBeispiel-ID-002\n";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'konzern_id_vorlage.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleScanComplete = (scannedValue: string) => {
    setNewKonzernId(scannedValue);
    setShowScanner(false);
  };

  const filteredKonzernIds = konzernIds.filter(kid => 
    kid.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Hash className="w-6 h-6 text-purple-600 shrink-0" />
            <span className="break-words">Konzern IDs verwalten</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Search and Add */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Konzern ID suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
          </div>

          {/* Add new Konzern ID form */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-800 mb-3 break-words">Neue Konzern ID hinzufügen</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  placeholder="Konzern ID eingeben"
                  value={newKonzernId}
                  onChange={(e) => setNewKonzernId(e.target.value)}
                  className="flex-1 p-2 border border-gray-300 rounded-lg"
                />
                <button
                  onClick={() => setShowScanner(true)}
                  className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  title="Barcode scannen"
                >
                  <QrCode className="w-5 h-5" />
                </button>
              </div>
              <button
                onClick={handleAddKonzernId}
                disabled={!newKonzernId.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed flex items-center gap-1 shrink-0"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span>Hinzufügen</span>
              </button>
            </div>
          </div>

          {/* Import/Export section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-3 break-words">Import/Export</h3>
            
            <div className="flex flex-col gap-4">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excel/CSV-Datei importieren
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <input
                      type="file"
                      id="konzern-id-file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                    <label
                      htmlFor="konzern-id-file"
                      className="cursor-pointer flex items-center justify-center w-full p-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 truncate"
                    >
                      <Upload className="w-4 h-4 mr-2 shrink-0" />
                      <span className="text-sm truncate">
                        {importFile ? importFile.name : 'Datei auswählen'}
                      </span>
                    </label>
                  </div>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || isImporting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed shrink-0"
                  >
                    {isImporting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Importieren'
                    )}
                  </button>
                </div>
                
                {importStatus === 'success' && (
                  <div className="mt-2 p-2 bg-green-100 text-green-700 rounded-md flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 shrink-0" />
                    <span className="text-sm break-words">{importMessage}</span>
                  </div>
                )}
                
                {importStatus === 'error' && (
                  <div className="mt-2 p-2 bg-red-100 text-red-700 rounded-md flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 shrink-0" />
                    <span className="text-sm break-words">{importMessage}</span>
                  </div>
                )}
              </div>
              
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vorlage herunterladen
                </label>
                <button
                  onClick={handleExportTemplate}
                  className="flex items-center justify-center w-full p-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <Download className="w-4 h-4 mr-2 shrink-0" />
                  <span className="text-sm">CSV-Vorlage herunterladen</span>
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Laden Sie eine Vorlage herunter, um Ihre Konzern IDs zu formatieren.
                </p>
              </div>
            </div>
          </div>

          {/* Konzern IDs list */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Konzern ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zugewiesen an
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Aktionen</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredKonzernIds.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                        Keine Konzern IDs gefunden
                      </td>
                    </tr>
                  ) : (
                    filteredKonzernIds.map(konzernId => (
                      <tr key={konzernId.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <Hash className="h-5 w-5 text-purple-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 break-words">
                                {konzernId.value}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            konzernId.isUsed 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {konzernId.isUsed ? 'Verwendet' : 'Verfügbar'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {konzernId.isUsed ? (
                            <span>
                              {konzernId.assignedAt && (
                                <span className="text-xs text-gray-400 block">
                                  {new Date(konzernId.assignedAt).toLocaleDateString('de-DE')}
                                </span>
                              )}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {!konzernId.isUsed && (
                            <button
                              onClick={() => handleDeleteKonzernId(konzernId.value)}
                              className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                              title="Konzern ID löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>

      {showScanner && (
        <BarcodeScanner
          onScan={handleScanComplete}
          onClose={() => setShowScanner(false)}
          title="Konzern ID scannen"
          description="Scannen Sie den Barcode oder QR-Code der Konzern ID"
        />
      )}
    </div>
  );
};